import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'

export type Pipeline = 'marketing' | 'sales' | 'post_sales' | 'partnership' | 'podcast'
export type Level = 'green' | 'amber' | 'red'

export interface DealContactRef { contactId: string; role: string }

export interface Deal {
  id: string
  accountId: string
  accountName: string
  name: string
  pipeline: Pipeline
  stage: string
  stageLabel: string
  /** The board column it sits in, and its status there when the column has more than one. */
  group: string
  groupLabel: string
  statusName: string | null
  status: 'open' | 'won' | 'lost'
  value: number
  ownerId: string | null
  source: string | null
  products: string[]
  contacts: DealContactRef[]
  lostReason: string | null
  closedAt: string | null
  stageChangedAt: string
  nextStep: string | null
  nextStepAt: string | null
  checkInAt: string | null
  demoBy: string | null
  pilotStartAt: string | null
  pilotEndAt: string | null
  decisionAt: string | null
  successCriteria: string | null
  sponsorContactId: string | null
  projectId: string | null
  createdAt: string
  version: number
  fresh: { level: Level; reasons: string[]; daysInStage: number }
}

export interface Account { id: string; name: string; domain: string | null; industry: string | null; size: string | null; ownerId: string | null; renewalAt: string | null; notes: string | null; version: number }
export interface Contact { id: string; accountId: string; name: string; title: string | null; email: string | null; phone: string | null; version: number }
export interface Activity {
  id: string
  accountId: string
  dealId: string | null
  contactId: string | null
  kind: 'note' | 'email' | 'call' | 'meeting' | 'whatsapp' | 'task' | 'stage'
  direction: 'in' | 'out' | null
  subject: string | null
  body: string | null
  occurredAt: string
  authorId: string | null
  dueAt: string | null
  doneAt: string | null
  assigneeId: string | null
  version: number
}

export interface Meta {
  pipelines: Array<{ key: Pipeline; label: string; stages: Array<{ key: string; label: string; rule: string }>; groups: StageGroup[] }>
  lostReasons: string[]
  activityKinds: Array<Activity['kind']>
  contactRoles: string[]
  products: Array<{ key: string; label: string }>
  people: Array<{ id: string; name: string; email: string }>
  techLeadId: string | null
}

export interface Board {
  pipeline: Pipeline
  columns: Array<{ stage: string; label: string; rule: string; statuses: Array<{ key: string; label: string }>; count: number; value: number; deals: Deal[] }>
  closed: Deal[]
  forecast: { byStage: Array<{ stage: string; label: string; count: number; value: number; weighted: number }>; open: number; weighted: number } | null
  counts: Record<Pipeline, number>
}

export interface ProjectRef { id: string; name: string; phase: string; phaseLabel: string; status: string; cycle: number }

export interface Brief { problem: string | null; requirements: string | null; scope: string | null; aimStrategy: string | null }

export interface DealDetail {
  deal: Deal
  brief: Brief
  changeLog: Array<{ id: string; at: string; text: string; note: string | null; by: string | null; kind: 'dlog' | 'stage' }>
  account: Account | null
  contacts: Array<Contact & { role: string | null }>
  timeline: Activity[]
  project: ProjectRef | null
  gates: Record<string, string[]>
}

export interface StageGroup { key: string; label: string; rule: string; statuses: Array<{ key: string; label: string }> }

export interface AccountRow extends Account { contacts: number; openDeals: number; openValue: number; customer: boolean; where: { pipeline: Pipeline; stage: string; label: string } | null; parentId: string | null }
export interface AccountDetail {
  account: Account
  contacts: Contact[]
  deals: Deal[]
  timeline: Activity[]
  projects: ProjectRef[]
  parent: { id: string; name: string } | null
  children: Array<{ id: string; name: string; depth: number; lifecycle: string | null; openDeals: number; contacts: number; people: Array<{ id: string; name: string; title: string | null }> }>
  highlights: { lifecycle: string | null; owners: Array<{ id: string; name: string }>; source: string | null; firstSeen: string; lastActivityAt: string | null }
  treeDeals: Array<Deal & { via: string }>
}

export interface ContactRow extends Contact { accountName: string | null; deals: number }
export interface ContactPage {
  contact: Contact
  account: Account | null
  deals: Array<Deal & { role: string | null }>
  history: Array<Activity & { withThem: boolean }>
  colleagues: Contact[]
}

export interface Conversation extends Activity { accountName: string; contactName: string | null; contactEmail: string | null; dealName: string | null; dealOwnerId: string | null }

export interface DashItem { dealId: string; accountId: string; accountName: string; label: string; at: string }
export interface Dashboard {
  from: string
  to: string
  previousFrom: string
  kpis: Array<{ key: string; label: string; count: number; previous: number; value?: number; note: string | null; items: DashItem[] }>
  byOwner: Array<{ ownerId: string; name: string; accounts: number; openDeals: number; demos: number; pilots: number; won: number; wonValue: number }>
  attention: Array<{ key: string; label: string; items: DashItem[] }>
}

export interface Today {
  tasks: Array<Activity & { accountName: string }>
  pickup: Deal[]
  needsAction: Deal[]
  checkIns: Deal[]
  projectTasks: Array<{ id: string; projectId: string; projectName: string; title: string; team: string; dueAt: string | null; status: string; phase: string }>
  mine: { deals: number; salesValue: number }
}

export type MigrateScope = 'deals' | 'companies' | 'all'
export interface MigratePreview {
  readAt: string
  scope: MigrateScope
  counts: {
    opportunities: number
    byStatus: { open: number; won: number; lost: number; abandoned: number }
    contactsInGhl: number
    contactsOnDeals: number
    contactsWithCompany: number
    contactsComing: number
    companiesInGhl: number
    accountsToCreate: number
    alreadyImported: { deals: number }
  }
  landings: Array<{ pipeline: string; stage: string; status: string; count: number; from: string[] }>
  unknownStages: Array<{ deal: string; pipeline: string | null; stage: string | null; lands: string }>
  noAccount: string[]
  unmatchedOwners: Array<{ name: string; email: string | null; deals: number }>
  accountsWithManyDeals: Array<{ account: string; domain: string | null; deals: string[] }>
  likelyGroups: Array<{ domain: string; names: string[] }>
  work: number
}
export interface MigrateRun { accounts: number; contacts: number; deals: number; notes: number; tasks: number; projects: number; owners: number; skipped: number; errors: string[]; offset: number; next: number; total: number; done: boolean }
export interface Booking {
  enabled: boolean
  title: string
  durationMin: number
  memberIds: string[]
  timezone: string
  /** Seven days, Sunday first; each a list of [start, end) minutes after midnight. */
  hours: Array<Array<[number, number]>>
  bufferMin: number
  minNoticeHours: number
  daysAhead: number
  stepMin: 15 | 30 | 45 | 60
  maxPerDay: number
  description: string
}
export interface Settings { enabled: boolean; techLeadId: string | null; demoOwnerId: string | null; productTechLeads: Record<string, string | null>; booking: Booking; on: boolean; production: boolean }

export interface TeamHost { id: string; name: string; bookable: boolean; problem: string | null }
export interface Slots { timezone: string; durationMin: number; slots: Array<{ start: string; end: string; hostIds: string[] }>; hosts: TeamHost[] }
export interface BookingRow {
  id: string
  start: string
  end: string
  hostId: string | null
  hostName: string | null
  contact: { id: string; name: string; email: string | null; phone: string | null } | null
  account: { id: string; name: string } | null
  dealId: string | null
  subject: string | null
  notes: string | null
  meetLink: string | null
  bookedBy: string | null
  eventId: string
}
export interface TeamCalendar {
  timezone: string
  durationMin: number
  team: Array<TeamHost & { userId: string }>
  bookings: BookingRow[]
  busy: Record<string, Array<{ start: string; end: string }>>
}
export type QueueFilter = 'todo' | 'callbacks' | 'booked' | 'all'
export interface QueueItem {
  contactId: string
  name: string
  title: string | null
  email: string | null
  phone: string | null
  accountId: string
  accountName: string
  deal: { id: string; pipeline: string; stage: string; ownerId: string | null } | null
  attempts: number
  lastTouch: { at: string; kind: string; outcome: string | null; by: string | null } | null
  callbackAt: string | null
  callbackBy: string | null
  bookedAt: string | null
  closed: boolean
}
export const OUTCOMES = [
  ['no_answer', 'No answer'],
  ['left_message', 'Left a message'],
  ['talked', 'Talked, not booked yet'],
  ['call_back', 'Call back later'],
  ['not_interested', 'Not interested'],
  ['wrong_contact', 'Wrong person or number'],
] as const
export type Outcome = (typeof OUTCOMES)[number][0]
export interface BookBody { contactId: string; start: string; hostId?: string | null; anyTime?: boolean; notes?: string | null; notify?: boolean }

export interface ContactInput { name: string; title?: string | null; email?: string | null; phone?: string | null; role?: string }
export interface DealPatch {
  name?: string
  value?: number
  ownerId?: string | null
  source?: string | null
  products?: string[]
  nextStep?: string | null
  nextStepAt?: string | null
  checkInAt?: string | null
  demoBy?: string | null
  pilotStartAt?: string | null
  pilotEndAt?: string | null
  decisionAt?: string | null
  successCriteria?: string | null
}
export interface NewDeal extends DealPatch {
  pipeline: Pipeline
  stage: string
  accountId?: string | null
  account?: { name: string; domain?: string | null; industry?: string | null; size?: string | null; parentId?: string | null } | null
  contacts?: ContactInput[]
  force?: boolean
}

const enc = encodeURIComponent

export const crm2Api = {
  settings: () => apiGet<Settings>('crm2/settings'),
  saveSettings: (body: Partial<Pick<Settings, 'enabled' | 'techLeadId' | 'demoOwnerId' | 'productTechLeads'>> & { booking?: Partial<Booking> }) => apiPut<Settings>('crm2/settings', body),
  meta: () => apiGet<Meta>('crm2/meta'),
  today: () => apiGet<Today>('crm2/today'),
  board: (pipeline: Pipeline, closed: boolean) => apiGet<Board>('crm2/board', { pipeline, ...(closed ? { closed: '1' } : {}) }),
  deal: (id: string) => apiGet<DealDetail>(`crm2/deals/${enc(id)}`),
  createDeal: (body: NewDeal) => apiPost<Deal>('crm2/deals', body),
  patchDeal: (id: string, body: DealPatch) => apiPatch<Deal>(`crm2/deals/${enc(id)}`, body),
  move: (id: string, stage: string, patch?: DealPatch) => apiPost<Deal>(`crm2/deals/${enc(id)}/move`, { stage, patch }),
  handoff: (id: string, body: { ownerId: string; value: number; nextStep: string; nextStepAt: string; products?: string[] }) => apiPost<Deal>(`crm2/deals/${enc(id)}/handoff`, body),
  win: (id: string, body: { renewalAt?: string | null; ownerId?: string | null }) => apiPost<{ won: Deal; postSales: Deal }>(`crm2/deals/${enc(id)}/win`, body),
  lose: (id: string, body: { reason: string; note?: string | null; nurtureUntil?: string | null }) => apiPost<Deal>(`crm2/deals/${enc(id)}/lose`, body),
  reopen: (id: string) => apiPost<Deal>(`crm2/deals/${enc(id)}/reopen`),
  addDealContact: (id: string, body: ContactInput) => apiPost<Contact>(`crm2/deals/${enc(id)}/contacts`, body),
  saveBrief: (id: string, body: Brief) => apiPut<Brief>(`crm2/deals/${enc(id)}/brief`, body),
  setRole: (id: string, contactId: string, role: string | null) => apiPut<{ ok: true }>(`crm2/deals/${enc(id)}/contact-role`, { contactId, role }),
  accounts: (q: string) => apiGet<AccountRow[]>('crm2/accounts', q ? { q } : undefined),
  createAccount: (body: { name: string; domain?: string | null; industry?: string | null; size?: string | null; ownerId?: string | null; source?: string | null; notes?: string | null; parentId?: string | null; contacts?: ContactInput[] }) => apiPost<Account>('crm2/accounts', body),
  setParent: (id: string, parentId: string | null) => apiPut<{ id: string; parentId: string | null }>(`crm2/accounts/${enc(id)}/parent`, { parentId }),
  contacts: (q: string) => apiGet<ContactRow[]>('crm2/contacts', q ? { q } : undefined),
  contact: (id: string) => apiGet<ContactPage>(`crm2/contacts/${enc(id)}`),
  conversations: (f: { kind?: string; q?: string; mine?: boolean }) => apiGet<Conversation[]>('crm2/conversations', { ...(f.kind ? { kind: f.kind } : {}), ...(f.q ? { q: f.q } : {}), ...(f.mine ? { mine: '1' } : {}) }),
  dashboard: (from: string, to: string) => apiGet<Dashboard>('crm2/dashboard', { from, to }),
  duplicates: (name: string, domain: string) => apiGet<Account[]>('crm2/accounts/duplicates', { name, domain }),
  account: (id: string) => apiGet<AccountDetail>(`crm2/accounts/${enc(id)}`),
  patchAccount: (id: string, body: Partial<Omit<Account, 'id' | 'version'>>) => apiPatch<Account>(`crm2/accounts/${enc(id)}`, body),
  addAccountContact: (id: string, body: ContactInput) => apiPost<Contact>(`crm2/accounts/${enc(id)}/contacts`, body),
  patchContact: (id: string, body: Partial<ContactInput> & { archived?: boolean }) => apiPatch<Contact>(`crm2/contacts/${enc(id)}`, body),
  addActivity: (body: { accountId?: string; dealId?: string | null; contactId?: string | null; kind: string; direction?: 'in' | 'out' | null; subject?: string | null; body?: string | null; occurredAt?: string | null; dueAt?: string | null; assigneeId?: string | null }) =>
    apiPost<Activity>('crm2/activities', body),
  patchActivity: (id: string, body: { done?: boolean; subject?: string; body?: string | null; dueAt?: string | null; assigneeId?: string | null; archived?: boolean }) => apiPatch<Activity>(`crm2/activities/${enc(id)}`, body),
  migratePreview: (scope: MigrateScope) => apiPost<MigratePreview>('crm2/migrate/preview', { scope }, { timeout: 60_000 }),
  migrateRun: (body: { scope: MigrateScope; offset: number; limit: number }) => apiPost<MigrateRun>('crm2/migrate/run', body, { timeout: 60_000 }),
  calendar: (from: string, to: string) => apiGet<TeamCalendar>('crm2/calendar', { from, to }),
  slots: (from: string, to: string) => apiGet<Slots>('crm2/booking/slots', { from, to }),
  book: (body: BookBody) => apiPost<BookingRow & { dealId: string }>('crm2/bookings', body, { timeout: 30_000 }),
  moveBooking: (id: string, body: { start: string; hostId?: string | null; anyTime?: boolean; notify?: boolean }) => apiPost<{ ok: true }>(`crm2/bookings/${enc(id)}/move`, body, { timeout: 30_000 }),
  cancelBooking: (id: string, body: { reason?: string | null; notify?: boolean }) => apiPost<{ ok: true }>(`crm2/bookings/${enc(id)}/cancel`, body, { timeout: 30_000 }),
  outreach: (filter: QueueFilter, q: string) => apiGet<{ total: number; items: QueueItem[] }>('crm2/outreach', { filter, ...(q ? { q } : {}) }),
  logAttempt: (body: { contactId: string; channel: 'call' | 'email' | 'whatsapp'; outcome: Outcome; note?: string | null; callbackAt?: string | null }) => apiPost<{ id: string }>('crm2/outreach/attempts', body),
  seed: () => apiPost<{ created: number }>('crm2/samples'),
  clear: () => apiDelete<{ removed: number }>('crm2/samples'),
}

/** On here, for this person? Off, or no access: the TapCRMv2 and TapPM sections stay hidden. */
export function useCrm2Settings(enabled = true) {
  return useQuery({ queryKey: qk.crm2Settings(), queryFn: crm2Api.settings, enabled, staleTime: 5 * 60_000, retry: false })
}
export function useCrm2Meta() {
  return useQuery({ queryKey: qk.crm2Meta(), queryFn: crm2Api.meta, staleTime: 5 * 60_000 })
}
export function useCrm2Board(pipeline: Pipeline, closed: boolean) {
  return useQuery({ queryKey: qk.crm2Board(pipeline, closed), queryFn: () => crm2Api.board(pipeline, closed), staleTime: 15_000 })
}
export function useCrm2Deal(id: string | null) {
  return useQuery({ queryKey: qk.crm2Deal(id ?? ''), queryFn: () => crm2Api.deal(id!), enabled: Boolean(id), staleTime: 10_000 })
}
export function useCrm2Today() {
  return useQuery({ queryKey: qk.crm2Today(), queryFn: crm2Api.today, staleTime: 15_000 })
}
export function useCrm2Accounts(q: string) {
  return useQuery({ queryKey: qk.crm2Accounts(q), queryFn: () => crm2Api.accounts(q), staleTime: 15_000 })
}
export function useCrm2Account(id: string) {
  return useQuery({ queryKey: qk.crm2Account(id), queryFn: () => crm2Api.account(id), staleTime: 10_000 })
}

/** Every write: refresh TapCRMv2 and TapPM together, and say what failed in words. */
export function useCrm2Write<A, R>(fn: (args: A) => Promise<R>, success?: (r: R, args: A) => string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, args) => {
      const msg = success?.(r, args)
      if (msg) toast.success(msg)
      void qc.invalidateQueries({ queryKey: qk.crm2() })
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  })
}

export function useCrm2Contacts(q: string) {
  return useQuery({ queryKey: qk.crm2Contacts(q), queryFn: () => crm2Api.contacts(q), staleTime: 15_000 })
}
export function useCrm2Contact(id: string) {
  return useQuery({ queryKey: qk.crm2Contact(id), queryFn: () => crm2Api.contact(id), staleTime: 10_000 })
}
export function useCrm2Conversations(f: { kind?: string; q?: string; mine?: boolean }) {
  return useQuery({ queryKey: qk.crm2Conversations(f.kind ?? '', f.q ?? '', Boolean(f.mine)), queryFn: () => crm2Api.conversations(f), staleTime: 15_000, refetchInterval: 60_000 })
}
export function useCrm2Dashboard(from: string, to: string) {
  return useQuery({ queryKey: qk.crm2Dashboard(from, to), queryFn: () => crm2Api.dashboard(from, to), staleTime: 30_000 })
}
export function useCrm2Calendar(from: string, to: string) {
  return useQuery({ queryKey: qk.crm2Calendar(from, to), queryFn: () => crm2Api.calendar(from, to), staleTime: 30_000, refetchInterval: 2 * 60_000 })
}
export function useCrm2Slots(from: string, to: string, enabled = true) {
  return useQuery({ queryKey: qk.crm2Slots(from, to), queryFn: () => crm2Api.slots(from, to), enabled, staleTime: 30_000 })
}
export function useCrm2Outreach(filter: QueueFilter, q: string) {
  return useQuery({ queryKey: qk.crm2Outreach(filter, q), queryFn: () => crm2Api.outreach(filter, q), staleTime: 15_000 })
}
